import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Navigate } from "react-router-dom";
import PropertyAssetForm from "../components/PropertyAssetForm";
import PropertyAssetViewModal from "../components/PropertyAssetViewModal";
import PropertyAssetsListTable, {
  PROPERTY_ASSET_COLUMN_DEFS,
  getPropertyAssetCellText,
  getPropertyAssetViewText,
} from "../components/PropertyAssetsListTable";
import PropertyMasterPanel from "../components/masters/PropertyMasterPanel";
import {
  fetchPropertyAssets,
  fetchPropertyAssetById,
  deletePropertyAsset,
  fetchPropertyStatusDropdown,
  fetchPropertyCategories,
  fetchPropertyConditions,
  fetchPropertyStatuses,
  createPropertyCategory,
  updatePropertyCategory,
  deletePropertyCategory,
  createPropertyCondition,
  updatePropertyCondition,
  deletePropertyCondition,
  createPropertyStatus,
  updatePropertyStatus,
  deletePropertyStatus,
} from "../api/propertyAssetsApi";
import { getCsrfToken } from "../../../public/api/authApi";
import GlobalMetricCards from "../../../global/GlobalMetricCards";
import GlobalQuickJump from "../../../global/GlobalQuickJump";
import GlobalSuccessFlashModal from "../../../global/GlobalSuccessFlashModal";
import GlobalFeedback from "../../../global/GlobalFeedback";
import { GlobalModal } from "../../../global/GlobalModal";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
  GlobalDirectoryPaginationSection,
} from "../../../global/directory";
import { buildPropertyAssetMetricItems } from "../utils/propertyAssetStats";
import { printPropertyAsset } from "../utils/propertyAssetHelpers";
import { usePropertyAssetRole } from "../utils/usePropertyAssetRole";
import { PROPERTY_ASSETS_CONTROL_SUBMODULE_SLUG } from "../utils/propertyAssetPermissionSlugs";
import "../../user-builder/styles/userBuilder.css";
import "../../user-builder/styles/UserBuilderPage.css";
import "../styles/propertyAssetsMetrics.css";

const PAGE_SIZE = 10;
const COLUMN_STORAGE_KEY = "girinestham.propertyAssets.columnVisibility";

const loadSavedVisibility = () => {
  try {
    const raw = localStorage.getItem(COLUMN_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    /* ignore */
  }
  return null;
};

const PropertyAssetsControlPage = ({ screenSlug = PROPERTY_ASSETS_CONTROL_SUBMODULE_SLUG }) => {
  const perms = usePropertyAssetRole(screenSlug);
  const isRegisterScreen = screenSlug === "property-assets";

  const [activeQuickJump, setActiveQuickJump] = useState(null);
  const [lookupsVersion, setLookupsVersion] = useState(0);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [records, setRecords] = useState([]);
  const [statusMaster, setStatusMaster] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [directoryFilters, setDirectoryFilters] = useState({
    search: "",
    column: "",
    value: "",
  });
  const [page, setPage] = useState(1);
  const [editingRecord, setEditingRecord] = useState(null);
  const [viewRecord, setViewRecord] = useState(null);
  const [viewDetail, setViewDetail] = useState(null);
  const [loadingView, setLoadingView] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const [columnVisibility, setColumnVisibility] = useState(() => {
    const saved = loadSavedVisibility();
    const initial = {};
    for (const c of PROPERTY_ASSET_COLUMN_DEFS) {
      initial[c.id] = saved?.[c.id] ?? c.defaultVisible;
    }
    return initial;
  });

  const fetchRecords = useCallback(async () => {
    setLoadingRecords(true);
    setErrorMsg("");
    try {
      const [assetsRes, statusRes] = await Promise.all([
        fetchPropertyAssets(),
        fetchPropertyStatusDropdown(),
      ]);
      setRecords(assetsRes?.data || []);
      setStatusMaster(statusRes?.data || []);
    } catch (err) {
      setErrorMsg(err.message || "Failed to fetch property assets.");
    } finally {
      setLoadingRecords(false);
      setLoadingStats(false);
    }
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords, lookupsVersion]);

  const statsItems = useMemo(
    () => buildPropertyAssetMetricItems(records, statusMaster),
    [records, statusMaster],
  );

  const metricSkeletonCount = Math.max(3, 2 + (statusMaster?.length || 0));

  const valueOptions = useMemo(() => {
    if (!directoryFilters.column) return [];
    const set = new Set();
    records.forEach((row) => {
      const text = String(
        getPropertyAssetCellText(directoryFilters.column, row) || "—",
      ).trim();
      if (text) set.add(text);
    });
    return Array.from(set)
      .sort()
      .map((v) => ({ value: v, label: v }));
  }, [records, directoryFilters.column]);

  const filteredRecords = useMemo(() => {
    const q = directoryFilters.search.trim().toLowerCase();
    const col = directoryFilters.column;
    const val = directoryFilters.value.trim().toLowerCase();

    let rows = records.filter((row) => {
      const matchesSearch =
        !q ||
        PROPERTY_ASSET_COLUMN_DEFS.some((c) =>
          String(getPropertyAssetCellText(c.id, row) || "")
            .toLowerCase()
            .includes(q),
        );
      if (!matchesSearch) return false;
      if (!col || !val) return true;
      return (
        String(getPropertyAssetCellText(col, row) || "").toLowerCase() === val
      );
    });

    rows = [...rows].sort((a, b) => {
      const av = getPropertyAssetCellText("asset_code", a);
      const bv = getPropertyAssetCellText("asset_code", b);
      return String(bv).localeCompare(String(av), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    });

    return rows;
  }, [records, directoryFilters]);

  const totalPages = Math.max(1, Math.ceil(filteredRecords.length / PAGE_SIZE));
  const paginatedRecords = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredRecords.slice(start, start + PAGE_SIZE);
  }, [filteredRecords, page]);

  useEffect(() => {
    setPage(1);
  }, [directoryFilters]);

  const visibleCols = PROPERTY_ASSET_COLUMN_DEFS.filter((c) => columnVisibility[c.id]);
  const getExportData = useCallback(
    () => ({
      headers: visibleCols.map((c) => c.label),
      rows: filteredRecords.map((row) =>
        visibleCols.map((c) => getPropertyAssetCellText(c.id, row)),
      ),
    }),
    [filteredRecords, visibleCols],
  );

  const bumpMasterLookups = useCallback(() => {
    setLookupsVersion((v) => v + 1);
  }, []);

  const handleFormSuccess = (msg) => {
    setSuccessMsg(msg);
    setEditingRecord(null);
    fetchRecords();
  };

  const handleRequestEdit = (row) => {
    setEditingRecord(row);
    requestAnimationFrame(() => {
      document.getElementById("pa-create")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleView = async (row) => {
    setViewRecord(row);
    setLoadingView(true);
    try {
      const resp = await fetchPropertyAssetById(row.id);
      setViewDetail(resp?.data ?? resp);
    } catch (err) {
      setErrorMsg(err.message || "Failed to load asset details.");
      setViewRecord(null);
    } finally {
      setLoadingView(false);
    }
  };

  const handlePrint = useCallback(
    (row) => {
      const visibleCols = PROPERTY_ASSET_COLUMN_DEFS.filter((c) => columnVisibility[c.id]);
      const showImageColumn = columnVisibility.asset_image_url;
      const hasImage = !!row.asset_image_url;

      const fields = visibleCols
        .filter((c) => c.id !== "asset_image_url")
        .map((c) => ({
          label: c.label,
          value: String(getPropertyAssetViewText(c.id, row) ?? "—"),
        }));

      if (showImageColumn && !hasImage) {
        fields.push({ label: "Image", value: "—" });
      }

      printPropertyAsset(row, {
        fields,
        showImage: showImageColumn && hasImage,
      });
    },
    [columnVisibility],
  );

  const handleDelete = (id) => {
    const rec = records.find((m) => m.id === id);
    setRecordToDelete(rec);
    setShowConfirmModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!recordToDelete) return;
    setIsConfirming(true);
    setErrorMsg("");
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deletePropertyAsset(recordToDelete.id, csrfToken);
      setSuccessMsg("Property asset deleted successfully.");
      setShowConfirmModal(false);
      setRecordToDelete(null);
      fetchRecords();
    } catch (err) {
      setErrorMsg(err.message || "Failed to delete asset.");
    } finally {
      setIsConfirming(false);
    }
  };

  const QUICK_JUMP_ITEMS = [
    ...(perms.canView ? [{ key: "stats", label: "Metrics", targetId: "pa-stats" }] : []),
    ...(perms.canCreate || (editingRecord && perms.canEdit)
      ? [{ key: "create", label: "Create / Edit Asset", targetId: "pa-create" }]
      : []),
    ...(perms.canView ? [{ key: "list", label: "Directory", targetId: "pa-list" }] : []),
    ...(perms.canManageMasters
      ? [{ key: "masters", label: "Master tables", targetId: "pa-masters" }]
      : []),
  ];

  const detail = viewDetail || viewRecord;

  if (perms.permissionsLoading) {
    return (
      <div className="app-screen-guard app-screen-guard--loading">
        <p>Loading access…</p>
      </div>
    );
  }

  if (!perms.canView) {
    return <Navigate to="/user-login" replace />;
  }

  return (
    <div className="user-builder-shell">
      <header className="user-builder-hero">
        <div className="user-builder-hero__text">
          <p className="user-builder-hero__eyebrow">
            {isRegisterScreen ? "Sub Admin Area" : "Administrative Area"}
          </p>
          <h2 className="user-builder-hero__title">Property / Assets</h2>
          <p className="user-builder-hero__sub">
            {isRegisterScreen
              ? "Create new assets or edit name, location, condition, brand, and remarks on existing records."
              : "Track office property and assets, manage master data, and maintain the asset register."}
          </p>
        </div>
      </header>

      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />
      <GlobalFeedback type="error" text={errorMsg} />

      <div className="user-builder-layout">
        <div className="user-builder-layout__main">
          <div id="pa-stats" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Metrics</h3>
              <p className="user-builder-panel__hint">
                Totals plus one card per active asset status from master data.
              </p>
            </div>
            <div className="property-assets-metrics">
              <GlobalMetricCards
                items={statsItems}
                loading={loadingStats}
                skeletonCount={metricSkeletonCount}
                ariaLabel="Property assets quick statistics"
              />
            </div>
          </div>

          {(perms.canCreate || (editingRecord && perms.canEdit)) ? (
          <div id="pa-create" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
            <div className="user-builder-panel__head">
              <h3 className="user-builder-panel__title">Asset Entry</h3>
              <p className="user-builder-panel__hint">
                {isRegisterScreen
                  ? "Create a new property asset (full form) or select a record to edit name, location, condition, brand, and remarks only."
                  : "Create a new property asset or edit an existing one."}
              </p>
            </div>
            <PropertyAssetForm
              mode={isRegisterScreen ? "subAdmin" : "full"}
              lookupsRefreshKey={lookupsVersion}
              onSuccess={handleFormSuccess}
              editingRecord={editingRecord}
              onCancelEdit={() => setEditingRecord(null)}
            />
          </div>
          ) : null}

          <div
            id="pa-list"
            className="user-builder-panel property-assets-directory-panel"
            style={{ scrollMarginTop: "2rem" }}
          >
            <div className="user-builder-panel__head" style={{ marginBottom: "1rem" }}>
              <h3 className="user-builder-panel__title">Directory</h3>
              <p className="user-builder-panel__hint">
                Search, filter, and manage property assets.
              </p>
            </div>

            <GlobalDirectoryShell
              filters={
                <GlobalDirectoryFiltersSection
                  searchLabel="Search"
                  searchPlaceholder="Search assets"
                  searchValue={directoryFilters.search}
                  onSearchChange={(v) =>
                    setDirectoryFilters((prev) => ({ ...prev, search: v }))
                  }
                  columnLabel="Column"
                  columnValue={directoryFilters.column}
                  onColumnChange={(v) =>
                    setDirectoryFilters((prev) => ({ ...prev, column: v, value: "" }))
                  }
                  valueLabel="Value"
                  valueValue={directoryFilters.value}
                  onValueChange={(v) =>
                    setDirectoryFilters((prev) => ({ ...prev, value: v }))
                  }
                  valueOptions={valueOptions}
                  gridColumnDefs={PROPERTY_ASSET_COLUMN_DEFS}
                  onClear={() =>
                    setDirectoryFilters({ search: "", column: "", value: "" })
                  }
                  secondaryStart={
                    <GlobalDirectoryColumnPicker
                      columnDefs={PROPERTY_ASSET_COLUMN_DEFS}
                      visibility={columnVisibility}
                      onVisibilityChange={setColumnVisibility}
                      storageKey={COLUMN_STORAGE_KEY}
                    />
                  }
                  secondaryEnd={
                    perms.canExport ? (
                      <GlobalDirectoryExportMenu
                        getExportData={getExportData}
                        filenameBase="property_assets"
                      />
                    ) : null
                  }
                />
              }
              grid={
                <PropertyAssetsListTable
                  records={paginatedRecords}
                  loading={loadingRecords}
                  columnVisibility={columnVisibility}
                  statusMasters={statusMaster}
                  onView={handleView}
                  onEdit={perms.canEdit ? handleRequestEdit : undefined}
                  onDelete={perms.canDelete ? handleDelete : null}
                  onPrint={handlePrint}
                  canDelete={perms.canDelete}
                  canPrint
                />
              }
              pagination={
                <GlobalDirectoryPaginationSection
                  page={page}
                  totalPages={totalPages}
                  total={filteredRecords.length}
                  pageSize={PAGE_SIZE}
                  onPageChange={setPage}
                  label="Property assets pagination"
                />
              }
            />
          </div>

          {perms.canManageMasters ? (
            <div id="pa-masters" className="user-builder-panel" style={{ scrollMarginTop: "2rem" }}>
              <div className="user-builder-panel__head">
                <h3 className="user-builder-panel__title">Master Table Management</h3>
                <p className="user-builder-panel__hint">
                  Configure categories, conditions, and asset statuses.
                </p>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "2rem", marginTop: "1.5rem" }}>
                <PropertyMasterPanel
                  title="Property Categories"
                  nameField="category_name"
                  nameLabel="Category name"
                  fetchList={fetchPropertyCategories}
                  createItem={createPropertyCategory}
                  updateItem={updatePropertyCategory}
                  deleteItem={deletePropertyCategory}
                  onDirectoryChange={bumpMasterLookups}
                />
                <hr style={{ border: 0, borderTop: "1px solid #e5e7eb" }} />
                <PropertyMasterPanel
                  title="Property Conditions"
                  nameField="condition_name"
                  nameLabel="Condition name"
                  fetchList={fetchPropertyConditions}
                  createItem={createPropertyCondition}
                  updateItem={updatePropertyCondition}
                  deleteItem={deletePropertyCondition}
                  onDirectoryChange={bumpMasterLookups}
                />
                <hr style={{ border: 0, borderTop: "1px solid #e5e7eb" }} />
                <PropertyMasterPanel
                  title="Asset Statuses"
                  nameField="status_name"
                  nameLabel="Status name"
                  fetchList={fetchPropertyStatuses}
                  createItem={createPropertyStatus}
                  updateItem={updatePropertyStatus}
                  deleteItem={deletePropertyStatus}
                  onDirectoryChange={bumpMasterLookups}
                />
              </div>
            </div>
          ) : null}
        </div>

        <GlobalQuickJump
          asideAriaLabel="Property assets sections"
          items={QUICK_JUMP_ITEMS}
          activeKey={activeQuickJump}
          onActiveChange={setActiveQuickJump}
        />
      </div>

      <GlobalModal
        open={showConfirmModal}
        onClose={() => !isConfirming && setShowConfirmModal(false)}
        title="Confirm deletion"
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton onClick={() => setShowConfirmModal(false)} disabled={isConfirming}>
              Cancel
            </GlobalButton>
            <GlobalButton variant="danger" onClick={handleConfirmDelete} loading={isConfirming}>
              Confirm delete
            </GlobalButton>
          </div>
        }
      >
        <p>
          Delete asset <b>{recordToDelete?.asset_code}</b> — {recordToDelete?.property_asset_name}?
        </p>
      </GlobalModal>

      <PropertyAssetViewModal
        open={!!viewRecord}
        onClose={() => {
          setViewRecord(null);
          setViewDetail(null);
        }}
        detail={detail}
        loading={loadingView}
        columnVisibility={columnVisibility}
      />
    </div>
  );
};

export default PropertyAssetsControlPage;
